import * as React from 'react'
import {
  Root as DropdownMenuRoot,
  Trigger as DropdownMenuTriggerPrimitive,
  Group as DropdownMenuGroup,
  Portal as DropdownMenuPortal,
  Sub as DropdownMenuSub,
  RadioGroup as DropdownMenuRadioGroup,
  SubTrigger as DropdownMenuSubTriggerPrimitive,
  SubContent as DropdownMenuSubContentPrimitive,
  Content as DropdownMenuContentPrimitive,
  Item as DropdownMenuItemPrimitive,
  CheckboxItem as DropdownMenuCheckboxItemPrimitive,
  ItemIndicator as DropdownMenuItemIndicator,
  RadioItem as DropdownMenuRadioItemPrimitive,
  Label as DropdownMenuLabelPrimitive,
  Separator as DropdownMenuSeparatorPrimitive,
} from '@radix-ui/react-dropdown-menu'

import { cn } from '@/lib/utils'

const DropdownMenu = DropdownMenuRoot
const DropdownMenuTrigger = DropdownMenuTriggerPrimitive

const DropdownMenuSubTrigger: React.FC<
  React.ComponentProps<typeof DropdownMenuSubTriggerPrimitive> & {
    inset?: boolean
  }
> = ({ className, inset, children, ...props }) => (
  <DropdownMenuSubTriggerPrimitive
    className={cn(
      'flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <span className="ml-auto">›</span>
  </DropdownMenuSubTriggerPrimitive>
)
DropdownMenuSubTrigger.displayName = DropdownMenuSubTriggerPrimitive.displayName

const DropdownMenuSubContent: React.FC<
  React.ComponentProps<typeof DropdownMenuSubContentPrimitive>
> = ({ className, ...props }) => (
  <DropdownMenuSubContentPrimitive
    className={cn(
      'z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
)
DropdownMenuSubContent.displayName = DropdownMenuSubContentPrimitive.displayName

const DropdownMenuContent: React.FC<
  React.ComponentProps<typeof DropdownMenuContentPrimitive>
> = ({ className, sideOffset = 4, ...props }) => (
  <DropdownMenuPortal>
    <DropdownMenuContentPrimitive
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </DropdownMenuPortal>
)
DropdownMenuContent.displayName = DropdownMenuContentPrimitive.displayName

const DropdownMenuItem: React.FC<
  React.ComponentProps<typeof DropdownMenuItemPrimitive> & {
    inset?: boolean
  }
> = ({ className, inset, ...props }) => (
  <DropdownMenuItemPrimitive
    className={cn(
      'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
)
DropdownMenuItem.displayName = DropdownMenuItemPrimitive.displayName

const DropdownMenuCheckboxItem: React.FC<
  React.ComponentProps<typeof DropdownMenuCheckboxItemPrimitive>
> = ({ className, children, checked, ...props }) => (
  <DropdownMenuCheckboxItemPrimitive
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuItemIndicator>
        <span className="h-4 w-4">✓</span>
      </DropdownMenuItemIndicator>
    </span>
    {children}
  </DropdownMenuCheckboxItemPrimitive>
)
DropdownMenuCheckboxItem.displayName =
  DropdownMenuCheckboxItemPrimitive.displayName

const DropdownMenuRadioItem: React.FC<
  React.ComponentProps<typeof DropdownMenuRadioItemPrimitive>
> = ({ className, children, ...props }) => (
  <DropdownMenuRadioItemPrimitive
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuItemIndicator>
        <span className="h-2 w-2 rounded-full bg-current"></span>
      </DropdownMenuItemIndicator>
    </span>
    {children}
  </DropdownMenuRadioItemPrimitive>
)
DropdownMenuRadioItem.displayName = DropdownMenuRadioItemPrimitive.displayName

const DropdownMenuLabel: React.FC<
  React.ComponentProps<typeof DropdownMenuLabelPrimitive> & {
    inset?: boolean
  }
> = ({ className, inset, ...props }) => (
  <DropdownMenuLabelPrimitive
    className={cn(
      'px-2 py-1.5 text-sm font-semibold',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
)
DropdownMenuLabel.displayName = DropdownMenuLabelPrimitive.displayName

const DropdownMenuSeparator: React.FC<
  React.ComponentProps<typeof DropdownMenuSeparatorPrimitive>
> = ({ className, ...props }) => (
  <DropdownMenuSeparatorPrimitive
    className={cn('bg-muted -mx-1 my-1 h-px', className)}
    {...props}
  />
)
DropdownMenuSeparator.displayName = DropdownMenuSeparatorPrimitive.displayName

const DropdownMenuShortcut: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({
  className,
  ...props
}) => {
  return (
    <span
      className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
      {...props}
    />
  )
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
